import { Card, Switch } from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";

import { useFetchSuperviseesCaseload } from "../../../../providers/hooks/providers.queries";
import { useAppSelector } from "../../../../../hooks/useRedux";
import { IProviderSuperviseeCaseloadDashboard } from "../../../../providers/types/providers.types";
import ListState from "../../../../../components/ListState/ListState";
import { displayNameInRightFormat } from "../../../../../utils/helpers";
import SuperviseeTasks from "./SuperviseeTasks/SuperviseeTasks";
import { useFetchTasks } from "../../../../tasks/hooks/tasks.queries";
import {
    TaskCategory,
    TaskListSortAttribute,
    TaskStatus,
} from "../../../../tasks/types/tasks.types";
import { AppSortOrder } from "../../../../../utils/types";
import Task from "../../../../tasks/components/Task/Task";
import { useFetchUserDetails } from "../../../../../hooks/queries/user";
import Loader from "../../../../../shared-ui/Loader/Loader";

export default function SuperviseeTasksWrapper() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { practice } = useAppSelector((state) => state.userPractice);

    const loggedInUser = useFetchUserDetails();

    const { data, isLoading, error, isSuccess } = useFetchSuperviseesCaseload({
        providerId: practice?.provider_id as string,
        show_accepted_assignment_clients: "true",
        include_inactive_providers: "false",
    });

    const superviseeTasks = useFetchTasks(
        {
            status: [TaskStatus.IN_PROGRESS, TaskStatus.NOT_STARTED],
            assignee_user_ids: [loggedInUser?.data?.user_id ?? ""],
            user_id_query_logic: "OR",
            sort_attribute: TaskListSortAttribute.DUE_DATE,
            sort_order: AppSortOrder.ASC,
            category: [
                TaskCategory.REVIEW_SUPERVISEE_NOTE,
                TaskCategory.REVIEW_SUPERVISEE_ADDENDUM,
            ],
        },
        isSuccess
    );

    const extractedClientAppointmentArray = data?.data?.flatMap((item) =>
        item.client_appointments.map((innerItem) => ({
            client: innerItem.client,
            appointments: innerItem.appointments,
            provider: {
                provider_id: item.provider_id,
                first_name: item.first_name,
                last_name: item.last_name,
            },
        }))
    );

    const formattedData: IProviderSuperviseeCaseloadDashboard[] = [];

    extractedClientAppointmentArray?.forEach((item) => {
        const { client, provider } = item;

        item.appointments.forEach((innerItem) => {
            formattedData.push({
                ...client,
                ...innerItem,
                provider: {
                    provider_id: provider.provider_id,
                    first_name: provider.first_name,
                    last_name: provider.last_name,
                },
            });
        });
    });

    const handleCurrentSupervisee = (view: string) => {
        searchParams.set("supervisee", view);
        setSearchParams(searchParams);
    };

    const switchOptions = isSuccess
        ? data.data.map((provider) => ({
              title: displayNameInRightFormat({
                  firstName: provider.first_name,
                  lastName: provider.last_name,
              }),
              key: provider.provider_id,
              onClick: () => handleCurrentSupervisee(provider.provider_id),
          }))
        : [];

    const superviseeInView =
        searchParams.get("supervisee") || switchOptions[0]?.key || "";

    const superviseeNameToDisplay =
        formattedData.filter(
            (item) => item.provider.provider_id === superviseeInView
        ).length > 0
            ? displayNameInRightFormat({
                  firstName: formattedData.filter(
                      (item) => item.provider.provider_id === superviseeInView
                  )[0]?.provider.first_name,
                  lastName: formattedData.filter(
                      (item) => item.provider.provider_id === superviseeInView
                  )[0]?.provider.last_name,
              })
            : null;

    return (
        <>
            {isLoading ? <Loader /> : null}
            {error ? (
                <ListState
                    isError
                    errorMsg={
                        error?.response?.data?.message ||
                        "Could not load supervisor's clients at this time"
                    }
                    stateHelperText="Try reloading this page to solve this issue"
                    context="general"
                />
            ) : null}
            {isSuccess && data.data.length === 0 ? (
                <ListState
                    stateHelperText="No supervisor's client list to display"
                    isError={false}
                    emptyMessage="No supervisor's client"
                    context="general"
                />
            ) : null}
            {isSuccess && data.data.length > 0 ? (
                <>
                    <Card classNames="mb-[--spacing-header] max-w-full overflow-x-auto">
                        <Switch
                            activeSwitch={superviseeInView}
                            switchOptions={switchOptions}
                        />
                    </Card>
                    <div>
                        <SuperviseeTasks
                            appointments={formattedData.filter(
                                (item) =>
                                    item.provider.provider_id ===
                                    superviseeInView
                            )}
                            superviseeName={superviseeNameToDisplay}
                        />
                    </div>
                    {isSuccess ? (
                        <div>
                            <h3 className="mb-[--spacing-items] text-title font-medium text-text">
                                Notes Requiring Signature
                            </h3>
                            <Card classNames="max-w-full overflow-x-auto">
                                {superviseeTasks.isLoading ? <Loader /> : null}
                                {superviseeTasks.error ? (
                                    <ListState
                                        isError
                                        errorMsg={
                                            superviseeTasks.error?.response
                                                ?.data?.message ||
                                            "Could not load supervisee's pending tasks at this time"
                                        }
                                        stateHelperText="Try reloading this page to solve this issue"
                                        context="general"
                                    />
                                ) : null}
                                {superviseeTasks.isSuccess &&
                                superviseeTasks.data.data.length === 0 ? (
                                    <ListState
                                        stateHelperText="No supervisee's pending tasks found"
                                        isError={false}
                                        emptyMessage="No pending tasks"
                                        context="general"
                                    />
                                ) : null}
                                {superviseeTasks.isSuccess &&
                                superviseeTasks.data.data ? (
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {superviseeTasks.data.data.map(
                                            (task) => (
                                                <Task
                                                    task={task}
                                                    key={task.todo_id}
                                                />
                                            )
                                        )}
                                    </div>
                                ) : null}
                            </Card>
                        </div>
                    ) : null}
                </>
            ) : null}
        </>
    );
}
